/**
 * check if an object has a key with a non-null/undefined and not empty value
 * @param {Object} obj
 * @param {String} key
 * @returns bool
 */
const hasValidKey = (obj, key) => {
  return obj && obj[key] !== null && obj[key] !== undefined && obj[key] !== '';
};

/**
 * API returns `range: '-'` for untimed sessions (SB-8292). Treat that as
 * "no time" so we don't render a clock + bare dash (SB-8360).
 */
const isDisplayableRange = range => {
  if (range == null) return false;
  const trimmed = String(range).trim();
  return trimmed.length > 0 && trimmed !== '-';
};

/**
 * Creates a debounced version of a function that delays its execution
 * @param {Function} func - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {Function} - The debounced function
 */
const debounce = (func, delay) => {
  let timeoutId;

  return function debounced(...args) {
    const context = this;

    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
};

/**
 * Gets the plain text length from HTML content
 * @param {string} htmlContent - HTML content string
 * @returns {number} - Length of plain text content
 */
const getPlainTextLength = htmlContent => {
  if (!htmlContent) return 0;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  const plainText = tempDiv.textContent || tempDiv.innerText || '';
  return plainText.trim().length;
};

/**
 * Strips HTML tags from a string and returns plain text.
 * Uses DOMParser, which builds an inert document — unlike `innerHTML`, it never
 * fires event handlers (e.g. `<img onerror>`) or loads resources, so it is safe
 * to run over API-sourced HTML on every search pass.
 * @param {string} html - HTML string to strip
 * @returns {string} - Plain text content
 */
const stripHtml = html => {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

/**
 * Truncates HTML content to a specified character limit while preserving basic HTML structure
 * @param {string} htmlContent - The HTML content to truncate
 * @param {number} maxLength - Maximum number of characters (plain text)
 * @returns {string} - Truncated HTML content or original if within limit
 */
const truncateHtmlContent = (htmlContent, maxLength = 300) => {
  if (!htmlContent) return '';

  const plainTextLength = getPlainTextLength(htmlContent);
  if (plainTextLength <= maxLength) return htmlContent;

  // Create temporary element to work with HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Get plain text and truncate it
  const plainText = tempDiv.textContent || tempDiv.innerText || '';
  const truncatedPlainText = plainText.substring(0, maxLength).trim() + '...';

  // For truncated content, we'll return plain text with ellipsis
  // This ensures consistency and avoids broken HTML tags
  return truncatedPlainText;
};

/**
 * Determines if HTML content should show a "Show more" button
 * @param {string} htmlContent - The HTML content to check
 * @param {number} maxLength - Maximum number of characters (plain text)
 * @returns {boolean} - True if content exceeds the limit
 */
const shouldShowMoreButton = (htmlContent, maxLength = 300) => {
  if (!htmlContent) return false;
  return getPlainTextLength(htmlContent) > maxLength;
};

/**
 * Get the browser's timezone
 * @returns {string} The browser's timezone
 */
const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    // Fallback for older browsers
    return 'UTC';
  }
};

/**
 * Get the time from a date string in a specific timezone. It will be formatted in the client's language.
 * Null / invalid stamps must not become Unix epoch — `new Date(null)` is
 * 4:00 PM Pacific and was showing as a fake start on untimed children (SB-8292).
 * @param {string | null | undefined} date - The date string
 * @param {string} timezone - The timezone to use
 * @returns {string} The time string, or '' when there is no real clock time
 */
const getTimeInTimezone = (date, timezone = getBrowserTimezone()) => {
  if (date == null || date === '') return '';
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) return '';

  const locale =
    (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

  return dateObj.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
};

/**
 * Group schedule-card children for View Details. Untimed rows (API `range: '-'`)
 * share an empty key so they never inherit a 4:00 PM header from `Date(null)`
 * or from a sibling that actually starts at 4 PM (SB-8292).
 * @param {Array<{ range?: string, starts_at?: string | null }>} subsessions
 * @param {string} timezone
 * @returns {Record<string, typeof subsessions>}
 */
const groupSubsessionsByTime = (subsessions = [], timezone) =>
  subsessions.reduce((acc, subsession) => {
    const time = isDisplayableRange(subsession.range)
      ? getTimeInTimezone(subsession.starts_at, timezone)
      : '';
    if (!acc[time]) acc[time] = [];
    acc[time].push(subsession);
    return acc;
  }, {});

/**
 * Formats a speaker's display name with optional salutation and honorific.
 * Only includes salutation/honorific when the caller passes non-empty values
 * (i.e. when those fields are enabled in visibleSpeakerFields).
 * @param {string} salutation - e.g. "Dr." (pass '' to omit)
 * @param {string} fullName - the speaker's full name
 * @param {string} honorific - e.g. "PhD" (pass '' to omit)
 * @returns {string} formatted name, e.g. "Dr. Jane Smith, PhD"
 */
const getFormattedSpeakerName = (salutation, fullName, honorific) => {
  const s = salutation ? (salutation.endsWith('.') ? salutation + ' ' : salutation + '. ') : '';
  const h = honorific ? ', ' + honorific : '';
  return s + (fullName || '') + h;
};

/**
 * A session/subsession modal speaker's name, with salutation and honorific
 * only when those fields are visible in the embed — same rule as speaker cards.
 * @param {{ name?: string, salutation?: string, honorific?: string }} speaker
 * @param {string[]} visibleSpeakerFields
 */
const getSessionSpeakerDisplayName = (speaker, visibleSpeakerFields = []) =>
  getFormattedSpeakerName(
    visibleSpeakerFields.includes('salutation') ? speaker?.salutation : '',
    speaker?.name,
    visibleSpeakerFields.includes('honorific') ? speaker?.honorific : ''
  );

export {
  hasValidKey,
  getSessionSpeakerDisplayName,
  isDisplayableRange,
  debounce,
  getPlainTextLength,
  stripHtml,
  truncateHtmlContent,
  shouldShowMoreButton,
  getBrowserTimezone,
  getTimeInTimezone,
  groupSubsessionsByTime,
  getFormattedSpeakerName,
};
